import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CheckIn, UtcTime } from 'shared/models';
import { MemoryChart } from "./MemoryChart";
import { TemperatureChart } from "./TemperatureChart";
import { UsageChart } from "./UsageChart";
import { CpuChart } from "./CpuChart";
import { parseCheckInData } from "./parseCheckInData";
import { calculateThisSixHours, calculateNextQuarterHour, generateTicks, oneMinute, oneDay, threeDays, LoadingChart, formatTimeTickLabel, NoData } from "./CheckInCharts.lib";
import './CheckInCharts.css';
import { StorageChart } from "./StorageChart";
import { NetworkChart, NetworkData } from "./NetworkChart";

export type Props = {
    fnNow?(): UtcTime;
    isLoading: boolean;
    checkIns: CheckIn[];
    timeZone: string;
};

export const CheckInCharts = ({ fnNow, isLoading, checkIns, timeZone }: Props) => {
    const { t, i18n } = useTranslation();

    const now = (fnNow ?? Date.now)();
    const tzZone = moment.tz.zone(timeZone);
    const offset = tzZone ? tzZone.utcOffset(now) : 0;
    const thisSixHours = calculateThisSixHours(now - offset * oneMinute) + offset * oneMinute;
    const nextQuarterHour = calculateNextQuarterHour(now);

    //
    // Calculate all the chart data based on the checkins
    //
    const [ ticks, usageData, memoryData, storageData, networkData, temperatureData, cpuData, hasCpuUsage, hasStorageUsage, hasNetworkUsage ] = useMemo(() => {
        const ticks = generateTicks(thisSixHours);
    
        let hasCpuUsage = false;
        let hasStorageUsage = false;
        let hasNetworkUsage = false;
        // the check ins come in newest-first order, and a human-readable format.
        // So switch to JavaScript timestamp and sort by oldest first for the charts.
        const sortedCheckIns = [ ...checkIns ].sort((a, b) => a.utcTime - b.utcTime) || [];
        const sortedCheckInData = sortedCheckIns.map(c => parseCheckInData(c.data, c.utcTime)!).filter(d => !!d);

        const filteredData = sortedCheckInData.filter(d =>
            // Remove entries that would be too far to the left
            // (which causes the tick marks to move around and possibly collapse)
            d.serverTime >= nextQuarterHour - threeDays
        );

        const usageData = insertLateMarkers(filteredData.map(s => ({ name: s.serverTime, value: s.UsageCount })));
        const memoryData = insertLateMarkers(filteredData.map(s => ({ name: s.serverTime, value: 100 - s.FreeMemoryPct })));
        const rawTemperatureData = filteredData.map(s => ({ name: s.serverTime, value: s.Temperature }));

        const cpuData = insertLateMarkers(filteredData.map(s => {
            if (!hasCpuUsage && s.CpuUsage !== undefined && s.CpuUsage !== null) {
                hasCpuUsage = true;
            }
            return ({
                name: s.serverTime,
                value: (s.CpuUsage !== undefined && s.CpuUsage !== null) ? s.CpuUsage : null,
            });
        }));

        const storageData = insertLateMarkers(filteredData.map(s => {
            if (!hasStorageUsage && s.FreeStoragePct !== undefined && s.FreeStoragePct !== null) {
                hasStorageUsage = true;
            }
            return ({
                name: s.serverTime,
                value: (s.FreeStoragePct !== undefined && s.FreeStoragePct !== null) ? 100 - s.FreeStoragePct : null,
            });
        }));

        const networkData = insertLateNetworkMarkers(filteredData.map(s => {
            if (!hasNetworkUsage && s.TX !== undefined && s.TX !== null) {
                hasNetworkUsage = true;
            }
            return {
                name: s.serverTime,
                // RX and TX are represented as KB in checkin data,
                // so multiply by 1024 to represent bytes
                rx: (s.RX || 0) * 1024,
                tx: (s.TX || 0) * 1024,
            };
        }));

        // Add artificial measures at 100 and 0 so that all the colours of the heat
        // gradient will line up to the correct temperatures. And add one more fake
        // measure of null so the line won't be influenced by the fake data.
        const temperatureData = rawTemperatureData.length ? [
            { name: now - 4 * oneDay - 2, value: 100 },
            { name: now - 4 * oneDay - 1, value: 0 },
            { name: now - 4 * oneDay, value: null },
        ].concat(
            // Clip the measured temperatures to [0, 100] so that they don't mess up
            // the heat gradient. (values outside this range will change what colours
            // correspond to which temperatures). Also filter out temperatures below
            // 0 because they probably aren't real.
            insertLateMarkers(rawTemperatureData
                .filter(d => d.value > 0)
                .map(d => ({
                    ...d,
                    value: Math.max(0, Math.min(d.value, 100)),
                }))
            )
        ) : [];

        return [
            ticks,
            usageData,
            memoryData,
            storageData,
            networkData,
            temperatureData,
            cpuData,
            hasCpuUsage,
            hasStorageUsage,
            hasNetworkUsage,
        ];
    }, [ checkIns, thisSixHours, nextQuarterHour ]);

    return (
        <div id="check-in-charts">
            <div className="stat-name">{ t('DEVICE.STATUS.DEVICE_USAGE') }</div>
            <div className="stat-chart">
                { isLoading ? <LoadingChart/> : (usageData && usageData.length)
                    ? <UsageChart data={ usageData } ticks={ ticks } tickFormatter={ formatTimeTickLabel(timeZone, i18n) } fromDate={ nextQuarterHour - threeDays } toDate={ nextQuarterHour }/>
                    : <NoData/>
                }
            </div>

            { hasStorageUsage && (
                <>
                    <div className="stat-name">{ t('DEVICE.STATUS.STORAGE_USAGE') }</div>
                    <div className="stat-chart">
                        { isLoading ? <LoadingChart/> : (storageData && storageData.length)
                            ? <StorageChart data={ storageData } ticks={ ticks } tickFormatter={ formatTimeTickLabel(timeZone, i18n) } fromDate={ nextQuarterHour - threeDays } toDate={ nextQuarterHour }/>
                            : <NoData/>
                        }
                    </div>
                </>
            )}

            <div className="stat-name">{ t('DEVICE.STATUS.MEMORY_USAGE') }</div>
            <div className="stat-chart">
                { isLoading ? <LoadingChart/> : (memoryData && memoryData.length)
                    ? <MemoryChart data={ memoryData } ticks={ ticks } tickFormatter={ formatTimeTickLabel(timeZone, i18n) } fromDate={ nextQuarterHour - threeDays } toDate={ nextQuarterHour }/>
                    : <NoData/>
                }
            </div>

            <div className="stat-name">{ t('DEVICE.STATUS.TEMPERATURE') }</div>
            <div className="stat-chart">
                { isLoading ? <LoadingChart/> : (temperatureData && temperatureData.length)
                    ? <TemperatureChart data={ temperatureData } ticks={ ticks } tickFormatter={ formatTimeTickLabel(timeZone, i18n) } fromDate={ nextQuarterHour - threeDays } toDate={ nextQuarterHour }/>
                    : <NoData/>
                }
            </div>
            { hasCpuUsage && (
                <>
                    <div className="stat-name">{ t('DEVICE.STATUS.CPU_USAGE') }</div>
                    <div className="stat-chart">
                        { isLoading ? <LoadingChart/> : (cpuData && cpuData.length)
                            ? <CpuChart data={ cpuData } ticks={ ticks } tickFormatter={ formatTimeTickLabel(timeZone, i18n) } fromDate={ nextQuarterHour - threeDays } toDate={ nextQuarterHour } />
                            : <NoData/>
                        }
                    </div>
                </>
            )}
            
            { hasNetworkUsage && (
                <>
                    <div className="stat-name">{ t('DEVICE.STATUS.NETWORK_USAGE') }</div>
                    <div className="stat-chart">
                        { isLoading ? <LoadingChart/> : (networkData && networkData.length)
                            ? <NetworkChart data={ networkData } ticks={ ticks } timeZone={ timeZone } fromDate={ nextQuarterHour - threeDays } toDate={ nextQuarterHour } />
                            : <NoData/>
                        }
                    </div>
                </>
            )}
        </div>
    );
};

type Input = { name: number; value: number | null };

function insertLateMarkers(input: Input[]) {
    return input.reduce((items: Input[], item: Input, i: number) => {
        if (i === 0) {
            return [ item ];
        }
        const array = [ ...items ];

        // If there is more than a 30-minute gap then show a hole in the data
        if (item.name - input[i - 1].name > 30 * oneMinute) {
            // Terminate the line one millisecond past the previous checkin
            array.push({
                name: input[i - 1].name + 1,
                value: null,
            });
            // Add another null marker one millisecond before the next checkin
            array.push({
                name: item.name - 1,
                value: null,
            });
        }
        array.push(item);
        return array;
    }, []);
}

function insertLateNetworkMarkers(input: NetworkData[]) {
    return input.reduce((items: NetworkData[], item: NetworkData, i: number) => {
        if (i === 0) {
            return [ item ];
        }
        const array = [ ...items ];

        // If there is more than a 30-minute gap then show a hole in the data
        if (item.name - input[i - 1].name > 30 * oneMinute) {
            // Terminate the line one millisecond past the previous checkin
            array.push({
                name: input[i - 1].name + 1,
                rx: null,
                tx: null,
            });
            // Add another null marker one millisecond before the next checkin
            array.push({
                name: item.name - 1,
                rx: null,
                tx: null,
            });
        }
        array.push(item);
        return array;
    }, []);
}
